'use client';

/**
 * Compact Copy / PDF / Word button row.
 *
 * Backend exposes a single endpoint that returns three formats via
 * `?format=pdf|docx|txt`. Copy fetches `txt`, all three formats share the
 * same block model so wording and section ordering stay in lockstep.
 *
 * Pass the URL path WITHOUT the `?format=` suffix. The component appends it.
 */
import { Check, Copy, FileText, Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';

import { getAuthToken } from '@/lib/auth/store';
import { copyToClipboard } from '@/lib/copilot/export';
import { filenameFromDisposition, saveBlob } from '@/lib/download';

import { cn } from './cn';

export interface ExportActionsProps {
  /**
   * Path under the API base (no leading host), without query string.
   * Component appends `?format=…` per button.
   *
   * e.g. `/dd-rooms/{id}/analyses/{aid}/export`
   */
  exportPath: string;
  /** Filename root used if Content-Disposition is missing. */
  filenameBase: string;
  /** Optional className to align the row inside the parent. */
  className?: string;
  /** Compact mode shrinks padding for inline use under cards. */
  size?: 'sm' | 'md';
  /** Optional callback after a successful copy (e.g. toast). */
  onCopySuccess?: () => void;
  /** Optional callback on any error (e.g. toast). */
  onError?: (err: Error) => void;
}

type Format = 'pdf' | 'docx' | 'txt';

const FORMAT_EXT: Record<Format, string> = { pdf: 'pdf', docx: 'docx', txt: 'txt' };

async function fetchExport(
  path: string,
  format: Format,
): Promise<{ blob: Blob; filename: string; text?: string }> {
  const base = process.env['NEXT_PUBLIC_API_BASE_URL'];
  if (!base) throw new Error('API base URL not configured');
  const token = getAuthToken();
  const sep = path.includes('?') ? '&' : '?';
  const res = await fetch(`${base}${path}${sep}format=${format}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .catch(() => null)
      .then((d) => (d as { detail?: string } | null)?.detail);
    throw new Error(detail || `Export failed (HTTP ${res.status})`);
  }
  const filename = filenameFromDisposition(
    res.headers.get('content-disposition'),
    `${path.split('/').filter(Boolean).pop() ?? 'report'}.${FORMAT_EXT[format]}`,
  );
  if (format === 'txt') {
    const text = await res.text();
    return { blob: new Blob([text]), filename, text };
  }
  const blob = await res.blob();
  return { blob, filename };
}

function triggerSave(blob: Blob, filename: string): void {
  saveBlob(blob, filename);
}

export function ExportActions({
  exportPath,
  filenameBase,
  className,
  size = 'sm',
  onCopySuccess,
  onError,
}: ExportActionsProps) {
  const [busy, setBusy] = useState<Format | null>(null);
  const [copied, setCopied] = useState(false);

  const handleDownload = useCallback(
    async (format: 'pdf' | 'docx') => {
      setBusy(format);
      try {
        const { blob, filename } = await fetchExport(exportPath, format);
        triggerSave(
          blob,
          filename || `${filenameBase}.${FORMAT_EXT[format]}`,
        );
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        onError?.(e);
      } finally {
        setBusy(null);
      }
    },
    [exportPath, filenameBase, onError],
  );

  const handleCopy = useCallback(async () => {
    setBusy('txt');
    try {
      const { text } = await fetchExport(exportPath, 'txt');
      if (!text) throw new Error('No text returned');
      const ok = await copyToClipboard(text);
      if (!ok) throw new Error('Clipboard write blocked');
      setCopied(true);
      onCopySuccess?.();
      setTimeout(() => setCopied(false), 1_500);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      onError?.(e);
    } finally {
      setBusy(null);
    }
  }, [exportPath, onCopySuccess, onError]);

  const padding = size === 'sm' ? 'h-7 px-2 text-[11px]' : 'h-8 px-2.5 text-xs';
  const iconSize = size === 'sm' ? 12 : 13;

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5',
        className,
      )}
    >
      <ActionButton
        accent={copied ? 'emerald' : undefined}
        disabled={busy !== null}
        icon={
          copied ? (
            <Check size={iconSize} strokeWidth={2} />
          ) : busy === 'txt' ? (
            <Loader2 className="animate-spin" size={iconSize} strokeWidth={1.75} />
          ) : (
            <Copy size={iconSize} strokeWidth={1.75} />
          )
        }
        label={copied ? 'Copied' : 'Copy'}
        padding={padding}
        onClick={handleCopy}
      />
      <ActionButton
        disabled={busy !== null}
        icon={
          busy === 'pdf' ? (
            <Loader2 className="animate-spin" size={iconSize} strokeWidth={1.75} />
          ) : (
            <FileText size={iconSize} strokeWidth={1.75} />
          )
        }
        label="PDF"
        padding={padding}
        onClick={() => handleDownload('pdf')}
      />
      <ActionButton
        disabled={busy !== null}
        icon={
          busy === 'docx' ? (
            <Loader2 className="animate-spin" size={iconSize} strokeWidth={1.75} />
          ) : (
            <FileText size={iconSize} strokeWidth={1.75} />
          )
        }
        label="Word"
        padding={padding}
        onClick={() => handleDownload('docx')}
      />
    </div>
  );
}

function ActionButton({
  label,
  icon,
  onClick,
  disabled,
  padding,
  accent,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  padding: string;
  accent?: 'emerald';
}) {
  return (
    <button
      className={cn(
        'inline-flex items-center gap-1 rounded transition-colors',
        padding,
        accent === 'emerald'
          ? 'text-emerald-700'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
        'disabled:cursor-not-allowed disabled:opacity-60',
      )}
      disabled={disabled}
      type="button"
      onClick={onClick}
    >
      {icon}
      <span className="font-medium">{label}</span>
    </button>
  );
}

export default ExportActions;
